import React, { lazy, Suspense, useEffect } from "react";
import type { JSX } from "react";
import { BrowserRouter as Router, Route, Routes as Switch } from "react-router-dom";
import HomeComponent from "../page/default/home/home.component";
import { AuthProvider, useAuth } from "../contexts/auth-context";
import { NotificationProvider } from "../contexts/notification-context";
import { redirectToLoginHub } from "../utils/redirect-login-utils";
import LayoutComponent from "../components/layout/layout.component";
import Loading from "../components/loading/loading";
import UserComponent from "../page/account/user/user.component";
import UserItemComponent from "../page/account/user/user-item/user-item.component";
import GroupComponent from "../page/account/group/group.component";
import GroupItemComponent from "../page/account/group/group-item/group-item.component";
import BOMComponent from "../page/general/bom/bom.component";
import BomComponentComponent from "../page/general/bom/bom-component/bom-component.component";
import POComponent from "../page/general/po/po.component";
import StockComponent from "../page/general/stock/stock-component";
import ForecastPageComponent from "../page/general/forecast/forecast-page.component";
import ForecastItemComponent from "../page/general/forecast/forecast-item/forecast-item.component";
import ImportComponent from "../page/general/import/import.component";
import CustomerComponent from "../page/general/customer/customer.component";
import CustomerItemComponent from "../page/general/customer/customer-item/customer-item.component";
import AnalysisPageComponent from "../page/general/analysis/analysis-page.component";
import NewAnalysis from "../page/general/analysis/analysis-new.component";
import AnalysisProcessingPageComponent from "../page/general/analysis/components/analysis-processing.component";
import AnalysisErrorPageComponent from "../page/general/analysis/components/analysis-error.component";
import GdsCalculationComponent from "../page/general/gds-calculation/gds-calculation.component";
import GdsSimulationComponent from "../page/general/gds-simulation/gds-simulation.component";
import DashboardPageComponent from "../page/general/dashboard/dashboard-page.component";
import AiDemandForecastPageComponent from "../page/general/ai-demand-forecast/ai-demand-forecast-page.component";
const MaterialsDemandsPageComponent = lazy(() => import("../page/general/materials-demands/materials-demands-page.component"));
const AnalysisLayoutComponent = lazy(() => import("../page/general/analysis/layout/analysis-layout.component"));
const MaterialStatusTabComponent = lazy(() => import("../page/general/analysis/material-status/material-status-tab.component"));
import ImportDataPageComponent from "../page/general/import/import-data/import-data-page.component";
import HelpComponent from "../page/general/help/help.component";
import NotFoundComponent from "../page/default/not-found/not-found.component";
import LoginComponent from "../page/default/login/login.component";
import { DocumentTitleProvider } from "../contexts/document-title-context";

export const Routes = () => {
  const Private = ({ children }: { children: React.ReactNode }): JSX.Element | null => {
    const { authenticated, loading } = useAuth();

    // Side effects (navigation) don't belong in the render body: doing it
    // here ran on every render pass (including React StrictMode's double
    // invocation) instead of exactly once when "not authenticated" becomes
    // true.
    useEffect(() => {
      if (!loading && !authenticated) {
        redirectToLoginHub();
      }
    }, [loading, authenticated]);

    if (loading) {
      return <Loading open={loading} />;
    }
    if (!authenticated) {
      return null;
    }
    return <LayoutComponent>{children}</LayoutComponent>;
  };

  return (
    <Router>
      <DocumentTitleProvider>
        <AuthProvider>
          <NotificationProvider>
            <Switch>
              {/* Public: reachable with no session, unlike everything wrapped in <Private>. */}
              <Route path="/login" element={<LoginComponent />} />

              <Route path="/" element={<Private><HomeComponent /></Private>} />
              <Route path="/home" element={<Private><HomeComponent /></Private>} />

              <Route path="/account/user" element={<Private><UserComponent /></Private>}></Route>
              <Route path="/account/user/:action" element={<Private><UserItemComponent /></Private>}></Route>
              <Route path="/general/analysis" element={<Private><AnalysisPageComponent /></Private>} />
              <Route path="/account/group" element={<Private><GroupComponent /></Private>}></Route>
              <Route path="/account/group/:action" element={<Private><GroupItemComponent /></Private>}></Route>

              <Route path="/general/analysis/:action" element={<Private><NewAnalysis /></Private>} />
              <Route path="/general/analysis/:action/processing" element={<Private><AnalysisProcessingPageComponent /></Private>} />
              <Route path="/general/analysis/:action/error" element={<Private><AnalysisErrorPageComponent /></Private>} />
              <Route path="/general/bom" element={<Private><BOMComponent /></Private>}></Route>
              <Route path="/general/bom/:action" element={<Private><BomComponentComponent /></Private>}></Route>
              <Route path="/general/stock" element={<Private><StockComponent /></Private>}></Route>
              <Route path="/general/forecast" element={<Private><ForecastPageComponent /></Private>}></Route>
              <Route path="/general/forecast/:id" element={<Private><ForecastItemComponent /></Private>}></Route>
              <Route path="/general/po" element={<Private><POComponent /></Private>}></Route>
              <Route path="/general/customer" element={<Private><CustomerComponent /></Private>}></Route>
              <Route path="/general/customer/:action" element={<Private><CustomerItemComponent /></Private>}></Route>

              <Route path="/general/import-data" element={<Private><ImportDataPageComponent /></Private>}></Route>
              <Route path="/general/import-spreadsheets" element={<Private><ImportComponent mode="xls" /></Private>}></Route>

              <Route path="/general/analysis/:action/gds-simulation" element={<Private><GdsSimulationComponent /></Private>}></Route>

              {/* Unified Analysis layout: GDS Result, Items and Demand, Material Status */}
              <Route
                element={
                  <Private>
                    <Suspense fallback={<Loading open={true} />}>
                      <AnalysisLayoutComponent />
                    </Suspense>
                  </Private>
                }
              >
                <Route path="/general/analysis/:action/gds-calculation" element={<GdsCalculationComponent />} />
                <Route
                  path="/general/analysis/:action/materiais-demandas"
                  element={<MaterialsDemandsPageComponent isContextualTab={true} />}
                />
                <Route
                  path="/general/analysis/:action/material-status"
                  element={<MaterialStatusTabComponent />}
                />
              </Route>

              <Route path="/general/dashboard" element={<Private><DashboardPageComponent /></Private>}></Route>
              <Route path="/general/ai-demand-forecast" element={<Private><AiDemandForecastPageComponent /></Private>}></Route>
              <Route path="/general/materials-demands" element={<Private><Suspense fallback={<Loading open={true} />}><MaterialsDemandsPageComponent /></Suspense></Private>}></Route>
              <Route path="/general/import-sap" element={<Private><ImportComponent mode="sap" /></Private>}
              />

              <Route path="/general/help" element={<Private><HelpComponent /></Private>}></Route>

              <Route path="*" element={<NotFoundComponent />} />
            </Switch>
          </NotificationProvider>
        </AuthProvider>
      </DocumentTitleProvider>
    </Router>
  );
};
